import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { NotificationService } from '../../../_core/_services/notification.service';
import { ComplianceAdminService } from '../../../_core/_services/_compliance/compliance-admin.service';
import { ApiService } from '../../../_core/_services/api.service';

@Component({
  selector: 'app-admin-jurisdiction-form',
  standalone: false,
  templateUrl: './admin-jurisdiction-form.html',
  styleUrl: './admin-jurisdiction-form.css',
})
export class AdminJurisdictionForm implements OnInit {
  form!: FormGroup;
  loading = false;
  saving = false;
  error: string | null = null;

  id = '';
  isEditMode = false;

  countries: any[] = [];
  jurisdictions: any[] = [];
  selectedCountryId = '';
  hasExistingGlobalFallback = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: ComplianceAdminService,
    private api: ApiService,
    private notifyService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') || '';
    this.isEditMode = !!this.id;

    this.form = this.fb.group({
      countryId: [''],
      code: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(10)]],
      name: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]],
      regulatoryZone: [''],
      isActive: [true],
      isGlobalFallback: [false]
    });

    this.attachFormBehavior();
    this.loadCountriesAndJurisdictions();
  }

  private attachFormBehavior(): void {
    this.form.get('countryId')?.valueChanges.subscribe((countryId) => {
      if (this.form.get('isGlobalFallback')?.value) return;
      this.applySelectedCountry(countryId);
    });

    this.form.get('isGlobalFallback')?.valueChanges.subscribe((isGlobal) => {
      this.handleGlobalFallbackToggle(!!isGlobal);
    });
  }

  private loadCountriesAndJurisdictions(): void {
    this.loading = true;
    this.error = null;

    let countriesDone = false;
    let jurisdictionsDone = false;

    const finish = () => {
      if (countriesDone && jurisdictionsDone) {
        if (this.isEditMode) {
          this.loadExistingJurisdiction();
        } else {
          this.loading = false;
        }
      }
    };

    this.api.getCountries(1)
      .pipe(finalize(() => {
        countriesDone = true;
        finish();
      }))
      .subscribe({
        next: (res: any) => {
          this.countries = res?.data || [];
        },
        error: (err) => {
          this.error = err?.error?.Error?.Details || 'Failed to load countries.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });

    this.adminService.getJurisdictions({
      page: 1,
      pageSize: 500
    })
    .pipe(finalize(() => {
      jurisdictionsDone = true;
      finish();
    }))
    .subscribe({
      next: (res: any) => {
        this.jurisdictions = res?.data || res?.items || res || [];
        this.hasExistingGlobalFallback = this.jurisdictions.some((x: any) => !!x.isGlobalFallback);
      },
      error: (err) => {
        this.error = err?.error?.message || 'Failed to load jurisdictions.';
        this.notifyService.showError(this.error!, 'Error');
      }
    });
  }

  private loadExistingJurisdiction(): void {
    if (!this.id) return;

    this.adminService.getJurisdictionById(this.id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: any) => {
          const item = res?.data || res;

          if (!item) {
            this.error = 'Jurisdiction not found.';
            return;
          }

          const matchedCountry = this.findCountryByCode(item.code);

          this.form.patchValue({
            countryId: matchedCountry?.id || '',
            code: item.code || '',
            name: item.name || '',
            regulatoryZone: item.regulatoryZone || '',
            isActive: item.isActive ?? true,
            isGlobalFallback: item.isGlobalFallback ?? false
          }, { emitEvent: false });

          this.selectedCountryId = matchedCountry?.id || '';

          if (item.isGlobalFallback) {
            this.disableCountrySelection();
            this.setGlobalValues();
          } else {
            this.enableCountrySelection();
          }
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load jurisdiction.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  private findCountryByCode(code: string): any | null {
    const normalized = String(code || '').trim().toUpperCase();
    return this.countries.find((x: any) =>
      String(x.code || x.countryCode || '').trim().toUpperCase() === normalized
    ) || null;
  }

  private applySelectedCountry(countryId: string): void {
    this.selectedCountryId = countryId || '';

    const selected = this.countries.find((x: any) => String(x.id) === String(countryId));
    if (!selected) {
      this.form.patchValue({
        code: '',
        name: ''
      }, { emitEvent: false });
      return;
    }

    const code = selected.code || selected.countryCode || '';
    const name = selected.name || '';

    this.form.patchValue({
      code: String(code).trim().toUpperCase(),
      name: String(name).trim()
    }, { emitEvent: false });
  }

  private handleGlobalFallbackToggle(isGlobal: boolean): void {
    if (isGlobal) {
      if (this.hasAnotherGlobalFallback()) {
        this.form.patchValue({ isGlobalFallback: false }, { emitEvent: false });
        this.notifyService.showError(
          'A global fallback jurisdiction already exists. Only one global fallback is allowed.',
          'Validation'
        );
        return;
      }

      this.disableCountrySelection();
      this.setGlobalValues();
      return;
    }

    this.enableCountrySelection();

    if (this.selectedCountryId) {
      this.applySelectedCountry(this.selectedCountryId);
    } else {
      this.form.patchValue({
        code: '',
        name: ''
      }, { emitEvent: false });
    }
  }

  private disableCountrySelection(): void {
    this.form.get('countryId')?.disable({ emitEvent: false });
  }

  private enableCountrySelection(): void {
    this.form.get('countryId')?.enable({ emitEvent: false });
  }

  private setGlobalValues(): void {
    this.form.patchValue({
      countryId: '',
      code: 'GLOBAL',
      name: 'GLOBAL'
    }, { emitEvent: false });
  }

  hasAnotherGlobalFallback(): boolean {
    const existingGlobal = this.jurisdictions.find((x: any) => !!x.isGlobalFallback);
    if (!existingGlobal) return false;

    if (!this.isEditMode) return true;

    return String(existingGlobal.id) !== String(this.id);
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    const isGlobalFallback = !!this.form.get('isGlobalFallback')?.value;
    const countryId = this.form.get('countryId')?.value;

    if (!isGlobalFallback && !countryId) {
      this.notifyService.showError('Please select a country.', 'Validation');
      return;
    }

    if (isGlobalFallback && this.hasAnotherGlobalFallback()) {
      this.notifyService.showError(
        'A global fallback jurisdiction already exists. Only one global fallback is allowed.',
        'Validation'
      );
      return;
    }

    this.saving = true;
    this.error = null;

    const payload = {
      code: String(this.form.getRawValue().code || '').trim().toUpperCase(),
      name: String(this.form.getRawValue().name || '').trim(),
      regulatoryZone: String(this.form.value.regulatoryZone || '').trim() || null,
      isActive: !!this.form.value.isActive,
      isGlobalFallback
    };

    const request$ = this.isEditMode
      ? this.adminService.updateJurisdiction(this.id, payload)
      : this.adminService.createJurisdiction(payload);

    request$
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess('Jurisdiction saved successfully.', 'Success');
          this.router.navigate(['/admin/compliance/jurisdictions']);
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to save jurisdiction.';
        }
      });
  }

  cancel(): void {
    this.router.navigate(['/admin/compliance/jurisdictions']);
  }

  get countryId() { return this.form.get('countryId'); }
  get code() { return this.form.get('code'); }
  get name() { return this.form.get('name'); }
  get isGlobalFallback() { return this.form.get('isGlobalFallback'); }
}